/**
 * React Context and Hook for Device Updater Wizard
 *
 * Provides wizard state management to all child components via React Context.
 * Wraps the WizardStateMachine service for reactive state updates.
 */

import * as React from 'react';
import {
  WizardState,
  WizardContext,
  StateAction,
  StateActionType,
  DeviceInfo,
  UpdaterError,
  WizardStep,
  StepState,
  WIZARD_STEPS_ORDER,
} from '../types/wizard-types';
import { WizardStateMachine } from '../services/wizard-state-machine.service';
import { UpdaterLoggerService } from '../services/updater-logger.service';
import { ErrorRecoveryService } from '../services/error-recovery.service';
import { EIDValidationService } from '../services/eid-validation.service';
import { UpdateHistoryService } from '../services/update-history.service';
import { userAsPromise } from '../../../../data/providers/userProvider';

/**
 * Create the React Context for the wizard
 */
const WizardReactContext = React.createContext<WizardContext | null>(null);

/**
 * Props for the WizardProvider component
 */
export interface WizardProviderProps {
  children: React.ReactNode;
  /** Initial step to start at (dev mode only) */
  initialStep?: WizardStep;
}

/**
 * Get initial state for React
 */
function getInitialWizardState(): WizardState {
  const stepStates = new Map<WizardStep, StepState>();
  WIZARD_STEPS_ORDER.forEach((step, index) => {
    stepStates.set(step, index === 0 ? StepState.ACTIVE : StepState.PENDING);
  });

  return {
    currentStep: WizardStep.EID_ENTRY,
    stepStates,
    eid: null,
    eidValidated: false,
    deviceInfo: null,
    deviceConnected: false,
    currentProgress: 0,
    currentProgressMessage: '',
    error: null,
    canProceed: false,
    canGoBack: false,
    completed: false,
  };
}

/**
 * WizardProvider component
 *
 * Wraps children with wizard context, managing the state machine
 * and providing convenience methods for state updates.
 */
export const WizardProvider: React.FC<WizardProviderProps> = ({ children, initialStep }) => {
  // Initialize services
  const loggerRef = React.useRef<UpdaterLoggerService>(new UpdaterLoggerService());
  const stateMachineRef = React.useRef<WizardStateMachine>(
    new WizardStateMachine(loggerRef.current)
  );
  const errorRecoveryRef = React.useRef<ErrorRecoveryService>(
    new ErrorRecoveryService(loggerRef.current)
  );
  const eidValidationRef = React.useRef<EIDValidationService>(
    new EIDValidationService()
  );
  const updateHistoryRef = React.useRef<UpdateHistoryService>(
    new UpdateHistoryService()
  );

  // React state synced with state machine
  const [state, setState] = React.useState<WizardState>(getInitialWizardState);

  // Track previous step for detecting step changes
  const prevStepRef = React.useRef<WizardStep>(WizardStep.EID_ENTRY);

  // Initialize UpdateHistoryService with user context
  React.useEffect(() => {
    let cancelled = false;
    const initUpdateHistory = async () => {
      try {
        const user = await userAsPromise();
        if (!cancelled && user?.services?.firestore && user.orgId && user.id) {
          updateHistoryRef.current.initialize(
            user.services.firestore,
            user.orgId,
            user.id
          );
        }
      } catch (error) {
        if (!cancelled) {
          loggerRef.current.warning('Failed to initialize update history service', error);
        }
      }
    };
    initUpdateHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  // Subscribe to state machine changes
  React.useEffect(() => {
    const unsubscribe = stateMachineRef.current.subscribe((newState) => {
      setState(newState);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Skip to initial step if provided (dev mode)
  React.useEffect(() => {
    if (initialStep && initialStep !== WizardStep.EID_ENTRY) {
      const targetIndex = WIZARD_STEPS_ORDER.indexOf(initialStep);
      const completedSteps = WIZARD_STEPS_ORDER.slice(0, targetIndex);
      stateMachineRef.current.dispatch({
        type: StateActionType.SKIP_TO_STEP,
        step: initialStep,
        markCompletedSteps: completedSteps,
      });
    }
  }, []); // Only run on mount

  // Track update history in Firestore
  React.useEffect(() => {
    const updateHistory = async () => {
      if (!updateHistoryRef.current.isInitialized()) {
        return;
      }

      // Start session when user leaves EID_ENTRY step (clicks Continue)
      // This ensures we capture the final EID value, not intermediate keystrokes
      if (
        state.currentStep !== WizardStep.EID_ENTRY &&
        state.eid &&
        !updateHistoryRef.current.getSessionId()
      ) {
        try {
          await updateHistoryRef.current.startSession(state.eid);
          loggerRef.current.info('Update history session started', { eid: state.eid });
        } catch (error) {
          loggerRef.current.warning('Failed to start update history session', error);
        }
      }

      // Track step changes (after session is started)
      if (
        state.currentStep !== prevStepRef.current &&
        state.currentStep !== WizardStep.EID_ENTRY &&
        updateHistoryRef.current.getSessionId()
      ) {
        prevStepRef.current = state.currentStep;
        try {
          await updateHistoryRef.current.updateStep(state.currentStep);
          loggerRef.current.debug('Update history step recorded', { step: state.currentStep });
        } catch (error) {
          loggerRef.current.warning('Failed to update history step', error);
        }
      }

      // Track errors/failures
      if (state.error && updateHistoryRef.current.getSessionId()) {
        try {
          await updateHistoryRef.current.markFailed(state.error.code);
          loggerRef.current.info('Update history marked as failed', { errorCode: state.error.code });
        } catch (error) {
          loggerRef.current.warning('Failed to mark update history as failed', error);
        }
        return;
      }

      // Track successful completion
      if (state.completed && updateHistoryRef.current.getSessionId()) {
        try {
          await updateHistoryRef.current.markCompleted();
          loggerRef.current.info('Update history marked as completed');
        } catch (error) {
          loggerRef.current.warning('Failed to mark update history as completed', error);
        }
      }
    };

    updateHistory();
  }, [state.currentStep, state.eid, state.error, state.completed]);

  // Dispatch action to state machine
  const dispatch = React.useCallback((action: StateAction) => {
    stateMachineRef.current.dispatch(action);
  }, []);

  // Convenience methods
  const setEid = React.useCallback((eid: string) => {
    dispatch({ type: StateActionType.SET_EID, eid });
  }, [dispatch]);

  const validateEid = React.useCallback((valid: boolean) => {
    dispatch({ type: StateActionType.VALIDATE_EID, valid });
  }, [dispatch]);

  const setDeviceInfo = React.useCallback((deviceInfo: DeviceInfo) => {
    dispatch({ type: StateActionType.SET_DEVICE_INFO, deviceInfo });
  }, [dispatch]);

  const setProgress = React.useCallback((percent: number, message?: string) => {
    dispatch({ type: StateActionType.SET_PROGRESS, percent, message });
  }, [dispatch]);

  const setError = React.useCallback((error: UpdaterError) => {
    dispatch({ type: StateActionType.SET_ERROR, error });
  }, [dispatch]);

  const clearError = React.useCallback(() => {
    dispatch({ type: StateActionType.CLEAR_ERROR });
  }, [dispatch]);

  const nextStep = React.useCallback(() => {
    stateMachineRef.current.nextStep();
  }, []);

  const previousStep = React.useCallback(() => {
    stateMachineRef.current.previousStep();
  }, []);

  const skipToStep = React.useCallback((step: WizardStep, markCompletedSteps: WizardStep[]) => {
    dispatch({ type: StateActionType.SKIP_TO_STEP, step, markCompletedSteps });
  }, [dispatch]);

  const retry = React.useCallback(() => {
    if (state.error?.code) {
      errorRecoveryRef.current.resetRetryCounter(state.error.code);
    }
    dispatch({ type: StateActionType.RETRY_CURRENT_STEP });
  }, [dispatch, state.error]);

  const reset = React.useCallback(() => {
    errorRecoveryRef.current.resetAllCounters();
    updateHistoryRef.current.reset();
    prevStepRef.current = WizardStep.EID_ENTRY;
    dispatch({ type: StateActionType.RESET });
  }, [dispatch]);

  const complete = React.useCallback(() => {
    dispatch({ type: StateActionType.COMPLETE });
  }, [dispatch]);

  // Create context value
  const contextValue: WizardContext = React.useMemo(
    () => ({
      state,
      dispatch,
      setEid,
      validateEid,
      setDeviceInfo,
      setProgress,
      setError,
      clearError,
      nextStep,
      previousStep,
      skipToStep,
      retry,
      reset,
      complete,
    }),
    [
      state,
      dispatch,
      setEid,
      validateEid,
      setDeviceInfo,
      setProgress,
      setError,
      clearError,
      nextStep,
      previousStep,
      skipToStep,
      retry,
      reset,
      complete,
    ]
  );

  return (
    <WizardReactContext.Provider value={contextValue}>
      {children}
    </WizardReactContext.Provider>
  );
};

/**
 * Hook to access wizard context
 *
 * Must be used within a WizardProvider
 */
export function useWizard(): WizardContext {
  const context = React.useContext(WizardReactContext);

  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }

  return context;
}

/**
 * Hook to access the logger service
 */
export function useWizardLogger(): UpdaterLoggerService {
  // Create a single instance for the component tree
  const loggerRef = React.useRef<UpdaterLoggerService>(new UpdaterLoggerService());
  return loggerRef.current;
}

/**
 * Hook to access the EID validation service
 */
export function useEIDValidation(): EIDValidationService {
  const validationRef = React.useRef<EIDValidationService>(
    new EIDValidationService()
  );
  return validationRef.current;
}

/**
 * Hook to access the error recovery service
 */
export function useErrorRecovery(): ErrorRecoveryService {
  const loggerRef = React.useRef<UpdaterLoggerService>(new UpdaterLoggerService());
  const recoveryRef = React.useRef<ErrorRecoveryService>(
    new ErrorRecoveryService(loggerRef.current)
  );
  return recoveryRef.current;
}

export { WizardReactContext };
